import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Row, Col, Card, Badge, Button, Modal, Form, Spinner, Alert, Tabs, Tab } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';

function DonationManagement() {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [donations, setDonations] = useState([]);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    // Check Firebase Auth state - ทุกคนที่ login ได้เป็น admin อัตโนมัติ
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    const q = query(collection(db, "donations"), orderBy("createdAt", "desc"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const donationsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setDonations(donationsData);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists() && userSnap.data().role === "admin") {
        setIsAdmin(true);
        localStorage.setItem("userToken", user.uid);
      } else {
        setError("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
        await signOut(auth);
      }
    } catch (error) {
      setError("เข้าสู่ระบบล้มเหลว: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      setIsAdmin(false);
      localStorage.removeItem("userToken");
    });
  };

  const handleShowDetail = (donation) => {
    setSelectedDonation(donation);
    setAdminNote(donation.note || "");
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedDonation) return;

    setActionLoading(true);
    try {
      const donationRef = doc(db, "donations", selectedDonation.id);
      await updateDoc(donationRef, {
        status: "approved",
        approvedAt: serverTimestamp(),
        approvedBy: auth.currentUser.email,
        note: adminNote
      });

      setSuccessMessage(`อนุมัติการบริจาคของ ${selectedDonation.donorName} สำเร็จ`);
      setShowModal(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setError("เกิดข้อผิดพลาด: " + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedDonation) return;
    if (!adminNote.trim()) {
      setError("กรุณาใส่เหตุผลในการปฏิเสธ");
      return;
    }

    setActionLoading(true);
    try {
      const donationRef = doc(db, "donations", selectedDonation.id);
      await updateDoc(donationRef, {
        status: "rejected",
        approvedAt: serverTimestamp(),
        approvedBy: auth.currentUser.email,
        note: adminNote
      });

      setSuccessMessage(`ปฏิเสธการบริจาคของ ${selectedDonation.donorName}`);
      setShowModal(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setError("เกิดข้อผิดพลาด: " + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (donationId, donorName) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ที่จะลบการบริจาคของ ${donorName}?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, "donations", donationId));
      setSuccessMessage(`ลบการบริจาคของ ${donorName} สำเร็จ`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setError("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(timestamp).toLocaleString('th-TH');
    } catch (error) {
      return "N/A";
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'warning', text: i18n.language === 'en' ? '⏳ Pending' : '⏳ รออนุมัติ' },
      approved: { bg: 'success', text: i18n.language === 'en' ? '✅ Approved' : '✅ อนุมัติแล้ว' },
      rejected: { bg: 'danger', text: i18n.language === 'en' ? '❌ Rejected' : '❌ ปฏิเสธ' }
    };
    return styles[status] || styles.pending;
  };

  const filteredDonations = donations.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const stats = {
    total: donations.length,
    pending: donations.filter(d => d.status === 'pending').length,
    approved: donations.filter(d => d.status === 'approved').length,
    rejected: donations.filter(d => d.status === 'rejected').length,
    totalAmount: donations
      .filter(d => d.status === 'approved')
      .reduce((sum, d) => sum + (d.amount || 0), 0)
  };

  if (!isAdmin) {
    return (
      <Container className="py-5" style={{ minHeight: "100vh", paddingTop: "100px" }}>
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="shadow-lg">
              <Card.Body className="p-5 text-center">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔐</div>
                <h2 style={{ marginBottom: '1rem' }}>เข้าสู่ระบบจัดการการบริจาค</h2>
                <p style={{ color: '#666', marginBottom: '2rem' }}>
                  กรุณาเข้าสู่ระบบด้วยบัญชี Admin เพื่อเข้าถึงหน้านี้
                </p>
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => window.location.href = '/login'}
                  style={{ 
                    padding: '12px 40px',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}
                >
                  ไปหน้าล็อกอิน
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div style={{ paddingTop: "100px", minHeight: "100vh", background: "#f8f9fa" }}>
      <Container fluid className="py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0">💰 จัดการการบริจาค</h1>
          <Button variant="danger" onClick={handleLogout}>
            {i18n.language === 'en' ? 'Logout' : 'ออกจากระบบ'}
          </Button>
        </div>

        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Alert variant="success" className="mb-4">
                {successMessage}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center shadow-sm">
              <Card.Body>
                <h6 className="text-muted">{i18n.language === 'en' ? 'Total Donations' : 'การบริจาคทั้งหมด'}</h6>
                <h2 className="mb-0">{stats.total}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-warning">
              <Card.Body>
                <h6 className="text-muted">{i18n.language === 'en' ? 'Pending' : 'รออนุมัติ'}</h6>
                <h2 className="mb-0 text-warning">{stats.pending}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-success">
              <Card.Body>
                <h6 className="text-muted">{i18n.language === 'en' ? 'Approved' : 'อนุมัติแล้ว'}</h6>
                <h2 className="mb-0 text-success">{stats.approved}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-primary">
              <Card.Body>
                <h6 className="text-muted">{i18n.language === 'en' ? 'Total Amount Approved' : 'ยอดรวมที่อนุมัติ'}</h6>
                <h2 className="mb-0 text-primary">
                  ฿{stats.totalAmount.toLocaleString('th-TH')}
                </h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter Tabs */}
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Tabs activeKey={filter} onSelect={(k) => setFilter(k)} className="mb-0">
              <Tab eventKey="all" title={`${i18n.language === 'en' ? 'All' : 'ทั้งหมด'} (${stats.total})`} />
              <Tab eventKey="pending" title={`${i18n.language === 'en' ? 'Pending' : 'รออนุมัติ'} (${stats.pending})`} />
              <Tab eventKey="approved" title={`${i18n.language === 'en' ? 'Approved' : 'อนุมัติแล้ว'} (${stats.approved})`} />
              <Tab eventKey="rejected" title={`${i18n.language === 'en' ? 'Rejected' : 'ปฏิเสธ'} (${stats.rejected})`} />
            </Tabs>
          </Card.Body>
        </Card>

        {/* Donations List */}
        <Row>
          {filteredDonations.length === 0 ? (
            <Col>
              <Card className="text-center shadow-sm">
                <Card.Body className="py-5">
                  <h4 className="text-muted">ไม่มีข้อมูลการบริจาค</h4>
                </Card.Body>
              </Card>
            </Col>
          ) : (
            filteredDonations.map((donation, index) => (
              <Col md={6} lg={4} key={donation.id} className="mb-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="mb-1">{donation.donorName}</h5>
                          <small className="text-muted">{donation.email}</small>
                        </div>
                        <Badge bg={getStatusBadge(donation.status).bg}>
                          {getStatusBadge(donation.status).text}
                        </Badge>
                      </div>

                      <div className="mb-3">
                        <p className="mb-1">
                          <strong>📞 เบอร์:</strong> {donation.phone}
                        </p>
                        <p className="mb-1">
                          <strong>💰 จำนวน:</strong>{" "}
                          <span className="text-success fw-bold">
                            ฿{donation.amount?.toLocaleString('th-TH')}
                          </span>
                        </p>
                        <p className="mb-1">
                          <strong>📅 วันที่:</strong>{" "}
                          <small>{formatDate(donation.createdAt)}</small>
                        </p>
                        {donation.approvedBy && (
                          <p className="mb-1">
                            <strong>👤 โดย:</strong>{" "}
                            <small>{donation.approvedBy}</small>
                          </p>
                        )}
                      </div>

                      <div className="d-grid gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowDetail(donation)}
                        >
                          🔍 ดูรายละเอียด
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(donation.id, donation.donorName)}
                        >
                          🗑️ {i18n.language === 'en' ? 'Delete' : 'ลบ'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))
          )}
        </Row>
      </Container>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>📋 รายละเอียดการบริจาค</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDonation && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>ชื่อผู้บริจาค:</strong> {selectedDonation.donorName}</p>
                  <p><strong>อีเมล:</strong> {selectedDonation.email}</p>
                  <p><strong>เบอร์โทร:</strong> {selectedDonation.phone}</p>
                </Col>
                <Col md={6}>
                  <p><strong>จำนวนเงิน:</strong> <span className="text-success fw-bold">฿{selectedDonation.amount?.toLocaleString('th-TH')}</span></p>
                  <p><strong>วันที่บริจาค:</strong> {formatDate(selectedDonation.createdAt)}</p>
                  <p>
                    <strong>สถานะ:</strong>{" "}
                    <Badge bg={getStatusBadge(selectedDonation.status).bg}>
                      {getStatusBadge(selectedDonation.status).text}
                    </Badge>
                  </p>
                </Col>
              </Row>

              {/* Slip Image */}
              <div className="mb-3">
                <strong>สลิปการโอนเงิน:</strong>
                <div className="mt-2 text-center">
                  <img
                    src={selectedDonation.slipURL}
                    alt="Payment Slip"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "500px",
                      border: "2px solid #dee2e6",
                      borderRadius: "8px",
                      cursor: "pointer"
                    }}
                    onClick={() => window.open(selectedDonation.slipURL, '_blank')}
                  />
                  <p className="text-muted mt-2">
                    <small>คลิกที่รูปเพื่อดูขนาดเต็ม</small>
                  </p>
                </div>
              </div>

              {/* Admin Note */}
              <Form.Group className="mb-3">
                <Form.Label><strong>หมายเหตุ:</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="เพิ่มหมายเหตุ (ถ้ามี)"
                />
              </Form.Group>

              {selectedDonation.note && (
                <Alert variant="info">
                  <strong>หมายเหตุเดิม:</strong> {selectedDonation.note}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            ปิด
          </Button>
          {selectedDonation?.status === 'pending' && (
            <>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : (i18n.language === 'en' ? '❌ Reject' : '❌ ปฏิเสธ')}
              </Button>
              <Button
                variant="success"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : (i18n.language === 'en' ? '✅ Approve' : '✅ อนุมัติ')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DonationManagement;
